export default function VerificationCTA() {
  const auditItems = [
    {
      icon: 'ri-scan-2-line',
      title: 'Smart Contract Deep Scan',
      desc: 'Full bytecode decompilation. Detects freeze, burn & honey pot logic invisible to standard wallets.',
      color: 'text-emerald-500',
    },
    {
      icon: 'ri-list-check-3',
      title: 'Blacklist Cross-Reference',
      desc: 'Real-time screening against 50+ global compliance databases: OFAC, FINMA, Circle, Tether.',
      color: 'text-emerald-500',
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Wallet Provenance Audit',
      desc: "Chain-of-custody analysis. Traces the asset's full transaction history for contamination.",
      color: 'text-emerald-500',
    },
    {
      icon: 'ri-file-shield-2-line',
      title: 'Instant BPI Certificate',
      desc: 'Swiss-certified PDF. Digitally signed by BPI Holding SA. Court-admissible. 90-day validity.',
      color: 'text-emerald-500',
    },
  ];

  return (
    <section id="verify-section" className="bg-white">

      {/* ===== PART 1: DARK NAVY TOKEN HERO STRIP ===== */}
      <div className="bg-[#001529] px-4 sm:px-10 lg:px-16 py-10 sm:py-14">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="flex flex-col items-center text-center mb-8 px-2">
            <span
              className="text-slate-400 text-[11px] font-bold tracking-[0.25em] uppercase mb-3"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Institutional ERC-20 Asset Audit Portal
            </span>
            <a
              href="https://mycryptoaml.com/verification"
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer group"
            >
              <h2
                className="text-[1.65rem] sm:text-3xl md:text-4xl font-black text-white leading-tight tracking-tight mb-2 group-hover:text-emerald-300 transition-colors duration-200"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Proceed to Asset Verification &amp; Compliance
              </h2>
            </a>
            <p className="text-slate-400 text-sm sm:text-base max-w-xl px-1" style={{ fontFamily: 'Inter, sans-serif' }}>
              5-minute automated deep-scan. Instant Swiss-backed BPI Compliance Certificate.
            </p>
          </div>

          {/* USDC / USDT Token Cards */}
          <div className="grid grid-cols-2 gap-3 sm:gap-5 max-w-sm sm:max-w-xl mx-auto mb-7 px-2 sm:px-0">
            {/* USDT */}
            <a
              href="https://mycryptoaml.com/verification"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-6 flex flex-col items-center text-center hover:border-emerald-500/40 transition-all duration-200 cursor-pointer"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-emerald-500 rounded-full flex items-center justify-center mb-3">
                <span className="text-white font-black text-xl sm:text-3xl">₮</span>
              </div>
              <div className="text-white font-black text-sm sm:text-lg mb-0.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                USDT
              </div>
              <div className="text-slate-400 text-xs font-semibold tracking-wide">ERC-20</div>
              <div className="mt-2 bg-emerald-500/15 border border-emerald-500/20 rounded-full px-2 py-0.5">
                <span className="text-emerald-400 text-[10px] font-bold">Tether · Verified</span>
              </div>
            </a>
            {/* USDC */}
            <a
              href="https://mycryptoaml.com/verification"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-6 flex flex-col items-center text-center hover:border-sky-500/40 transition-all duration-200 cursor-pointer"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-sky-500 rounded-full flex items-center justify-center mb-3">
                <span className="text-white font-black text-xl sm:text-3xl">$</span>
              </div>
              <div className="text-white font-black text-sm sm:text-lg mb-0.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                USDC
              </div>
              <div className="text-slate-400 text-xs font-semibold tracking-wide">ERC-20</div>
              <div className="mt-2 bg-sky-500/15 border border-sky-500/20 rounded-full px-2 py-0.5">
                <span className="text-sky-400 text-[10px] font-bold">Circle · Verified</span>
              </div>
            </a>
          </div>

          {/* Pricing badge */}
          <div className="flex justify-center mb-6 px-4">
            <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/25 rounded-full px-4 sm:px-5 py-2.5 text-center">
              <i className="ri-price-tag-3-line text-emerald-400 text-sm flex-shrink-0"></i>
              <span className="text-emerald-300 text-xs sm:text-sm font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>
                Verification fee: <span className="text-white">$0.10 – $0.80</span> per audit
              </span>
            </div>
          </div>

          {/* ===== MAIN CTA BUTTON — "Initiate Secure Verification" ===== */}
          <div className="flex justify-center px-4 sm:px-0">
            <a
              href="https://mycryptoaml.com/verification"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto flex items-center justify-center gap-3 bg-emerald-500 hover:bg-emerald-400 text-white px-8 sm:px-12 py-4 sm:py-5 rounded-xl font-black text-base sm:text-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              <i className="ri-shield-check-line text-xl"></i>
              Initiate Secure Verification
            </a>
          </div>

          {/* Trust row */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center gap-3 sm:gap-8 mt-6 pt-6 border-t border-white/10 px-2">
            {[
              { icon: 'ri-lock-2-line', text: 'Non-Custodial' },
              { icon: 'ri-timer-flash-line', text: '5-Minute Audit' },
              { icon: 'ri-bank-line', text: 'Swiss FINMA Aligned' },
              { icon: 'ri-award-line', text: 'Court-Admissible' },
            ].map((t, i) => (
              <div key={i} className="flex items-center gap-1.5 justify-center sm:justify-start">
                <i className={`${t.icon} text-emerald-400 text-sm`}></i>
                <span className="text-slate-400 text-xs font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>{t.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== PART 2: WHITE SECTION — Audit Process Cards ===== */}
      <div className="px-4 sm:px-10 lg:px-16 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-1 h-7 bg-emerald-500 rounded-full"></div>
            <span
              className="text-slate-500 text-xs font-bold tracking-[0.2em] uppercase"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              What We Audit
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {auditItems.map((item, i) => (
              <div
                key={i}
                className="border border-slate-200 bg-slate-50 rounded-xl p-5 sm:p-6 hover:border-emerald-300 transition-all duration-200"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-white border border-slate-200 rounded-xl mb-4">
                  <i className={`${item.icon} text-lg ${item.color}`}></i>
                </div>
                <h3
                  className="text-slate-800 font-bold text-sm leading-tight mb-2"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {item.title}
                </h3>
                <p className="text-slate-500 text-xs leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Certificate card with Verify Now */}
          <div className="mt-4 bg-[#001529] rounded-2xl p-5 sm:p-7 flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-award-fill text-2xl text-emerald-400"></i>
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <div className="text-white font-bold text-sm sm:text-base mb-0.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                BPI Compliance Certificate
              </div>
              <div className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                Issued under Swiss law by BPI Holding SA, Geneva. FATF &amp; FINMA aligned. 90-day validity. Digitally signed PDF issued instantly.
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="flex items-center gap-1.5 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-3 py-1">
                <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></div>
                <span className="text-emerald-400 text-xs font-bold">Live</span>
              </div>
              <a
                href="https://mycryptoaml.com/verification"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-5 py-2.5 rounded-lg font-bold text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shield-check-line"></i>
                Verify Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
